import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    deleteAccessAndRefreshTokens,
    persistRefreshToken,
    persistRefreshTokenExpiresAtUTC,
    persistToken,
    readToken,
} from '@store/services/localStorage.service.ts';
import { extractError } from '@utils/slice.ts';

import type { RefreshTokenRequest, RefreshTokenResponse } from './auth.api.ts';
import { postRefreshToken } from './auth.api.ts';

export interface AuthSlice {
    token: string | null;
    isLoading: boolean;
    error: Record<string, any> | null;
    isSessionExpiringSoon: boolean;
}

const initialState: AuthSlice = {
    isLoading: false,
    error: null,
    token: readToken(),
    isSessionExpiringSoon: false,
};

export const doLogout = createAsyncThunk<void, void>('auth/doLogout', async (_payload, { rejectWithValue }) => {
    try {
        deleteAccessAndRefreshTokens();
        return;
    } catch (err) {
        return rejectWithValue(err);
    }
});

export const refreshToken = createAsyncThunk<RefreshTokenResponse, RefreshTokenRequest, { rejectValue: any }>(
    'auth/refreshToken',
    (refreshTokenPayload, { rejectWithValue }) =>
        postRefreshToken(refreshTokenPayload)
            .then((res) => {
                persistToken(res.accessToken);
                persistRefreshToken(res.refreshToken);
                persistRefreshTokenExpiresAtUTC(res.refreshTokenExpiresAtUTC);
                return res;
            })
            .catch((err) => {
                return rejectWithValue(err);
            }),
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setIsSessionExpiringSoon: (state, action: PayloadAction<boolean>) => {
            state.isSessionExpiringSoon = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(doLogout.fulfilled, (state) => {
            state.token = null;
        });
        builder.addCase(doLogout.rejected, (state, action) => {
            state.error = extractError(action);
        });
        builder.addCase(refreshToken.fulfilled, (state) => {
            state.error = null;
        });
        builder.addCase(refreshToken.rejected, (state, action) => {
            state.error = extractError(action);
        });
    },
});

export const { setIsSessionExpiringSoon } = authSlice.actions;

export default authSlice.reducer;
